import { useState } from 'react';
import { Alert, AlertDescription, AlertIcon, Tag, Box, CloseButton, Flex, Icon, Text } from '@chakra-ui/react';
import { WarningIcon, SettingsIcon, CopyIcon } from '@chakra-ui/icons';
import { StatsForNerds, Alert as AlertType } from '../../types/stats';
import { parsePercentage, parseMemoryMB, parseStorageGB } from '../../utils/stats';

interface AlertsPanelProps {
  data: StatsForNerds;
  paused: boolean;
}

export function AlertsPanel({ data, paused }: AlertsPanelProps) {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());

  const generateAlerts = (): AlertType[] => {
    const alerts: AlertType[] = [];

    // CPU Alert
    const cpuLoad = parsePercentage(data.cpu.load);
    if (cpuLoad >= 85) {
      alerts.push({
        type: 'cpu',
        message: `High CPU usage: ${data.cpu.load}`,
        severity: 'danger',
        dismissible: true
      });
    }

    // Memory Alert
    const totalGB = parseStorageGB(data.memory.total);
    const usedGB = parseStorageGB(data.memory.used);
    const memoryUsage = totalGB > 0 ? (usedGB / totalGB) * 100 : 0;
    
    if (memoryUsage >= 90) {
      alerts.push({
        type: 'memory',
        message: `Low memory: ${Math.round(memoryUsage)}% used (${data.memory.free} free)`,
        severity: 'danger',
        dismissible: true
      });
    }

    // Disk Alerts
    data.disks.forEach(disk => {
      const usage = parsePercentage(disk.usage);
      if (usage >= 80) {
        alerts.push({
          type: 'disk',
          message: `Disk ${disk.mount} nearly full: ${disk.usage} used`,
          severity: usage >= 90 ? 'danger' : 'warning',
          dismissible: true
        });
      }
    });

    return alerts.filter(alert => 
      !dismissedAlerts.has(`${alert.type}-${alert.message}`)
    );
  };

  const alerts = generateAlerts();

  const dismissAlert = (alert: AlertType) => {
    const alertKey = `${alert.type}-${alert.message}`;
    setDismissedAlerts(prev => new Set([...prev, alertKey]));
  };

  const getAlertIcon = (type: AlertType['type']) => {
    switch (type) {
      case 'cpu': return <Icon as={SettingsIcon} />;
      case 'memory': return <Icon as={WarningIcon} />;
      case 'disk': return <Icon as={CopyIcon} />;
      default: return <Icon as={WarningIcon} />;
    }
  };

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box borderRadius="2xl">
      {alerts.map((alert, index) => (
        <Alert 
          key={`${alert.type}-${index}`}
          status={alert.severity === 'danger' ? 'error' : 'warning'}
          position="relative"
          mb={2}
        >
          {paused && (
            <Tag position="absolute" top={2} right={8} colorScheme="yellow">Paused</Tag>
          )}
          
          <Flex alignItems="center" justifyContent="space-between" w="100%">
            <Flex alignItems="center" gap={2}>
              {getAlertIcon(alert.type)}
              <AlertDescription fontWeight="medium">
                {alert.message}
              </AlertDescription>
            </Flex>
            
            {alert.dismissible && (
              <CloseButton onClick={() => dismissAlert(alert)} />
            )}
          </Flex>
        </Alert>
      ))}

      {alerts.length > 0 && (
        <Text fontSize="xs" color="gray.500" textAlign="center">
          {alerts.length} active alert{alerts.length !== 1 ? 's' : ''}
        </Text>
      )}
    </Box>
  );
}
